import { InnerPageActions } from "@/components/site/InnerPageActions";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import { submitInquiry } from "@/lib/submit-inquiry";
import { toast } from "sonner";

import { breadcrumbScript } from "@/lib/schema";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Contact | Avolus Cars Vancouver" },
      {
        name: "description",
        content:
          "Contact Avolus Cars in Vancouver. Email info@avoluscars.com or call 778-379-8881. Luxury car delivery across Metro Vancouver, YVR, Abbotsford and Whistler.",
      },
      { property: "og:title", content: "Contact Avolus Cars | Vancouver Concierge" },
      {
        property: "og:description",
        content:
          "Reach the Avolus concierge by phone or email. Open 7 days a week, 8AM-8PM. Luxury car delivery across Metro Vancouver.",
      },
      { property: "og:url", content: "https://avoluscars.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/contact" }],
    // AutoRental JSON-LD is emitted once at the root; only BreadcrumbList here.
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Contact", url: "https://avoluscars.com/contact" },
      ]),
    ],
  }),
  component: Contact,
});

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const name = String(fd.get("name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim();
    const message = String(fd.get("message") || "").trim();

    if (!name || name.length > 100) return toast.error("Please enter a valid name.");
    if (!email || email.length > 255 || !/^\S+@\S+\.\S+$/.test(email))
      return toast.error("Please enter a valid email.");
    if (phone && phone.length > 30) return toast.error("Phone number is too long.");
    if (!message || message.length > 2000)
      return toast.error("Message must be between 1 and 2000 characters.");

    setSubmitting(true);
    const submittedAt = new Date().toISOString();
    const { error } = await submitInquiry({ name, email, phone, message, submittedAt });

    if (error) {
      setSubmitting(false);
      toast.error("Something went wrong. Please try again or call us.");
      return;
    }

    setSubmitting(false);
    setSubmitted(true);
    // Trackable conversion URL rather than an in-place state change.
    navigate({ to: "/thank-you" });

  };


  return (
    <Layout inner closing={false}>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Contact Us</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground">
              Speak with our concierge.
            </h1>
            <p className="mt-6 text-foreground/70 max-w-xl">
              Reach us by phone, email, or send a message and we'll respond promptly during
              operating hours.
            </p>
            <div className="mt-6"><InnerPageActions label="Book online" /></div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-5">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <div className="space-y-8">
              <div>
                <div className="flex items-center gap-3 text-foreground mb-2">
                  <Phone size={16} />
                  <span className="text-xs uppercase tracking-[0.25em]">Phone</span>
                </div>
                <a href="tel:+17783798881" className="font-display text-2xl text-foreground hover:underline underline-offset-4">
                  778-379-8881
                </a>
              </div>
              <div>
                <div className="flex items-center gap-3 text-foreground mb-2">
                  <Mail size={16} />
                  <span className="text-xs uppercase tracking-[0.25em]">Email</span>
                </div>
                <a href="mailto:info@avoluscars.com" className="font-display text-2xl text-foreground hover:underline underline-offset-4">
                  info@avoluscars.com
                </a>
              </div>
              <div>
                <div className="flex items-center gap-3 text-foreground mb-3">
                  <Clock size={16} />
                  <span className="text-xs uppercase tracking-[0.25em]">Hours</span>
                </div>
                <ul className="space-y-1 text-sm text-foreground/70">
                  <li>Monday - Friday: <span className="text-foreground">8:00 AM - 8:00 PM</span></li>
                  <li>Saturday - Sunday: <span className="text-foreground">8:00 AM - 8:00 PM</span></li>
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-foreground mb-3">
                  Follow
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href="https://instagram.com/avoluscars"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    className="h-11 w-11 inline-flex items-center justify-center border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
                  >
                    <Instagram size={16} />
                  </a>
                  <a
                    href="https://facebook.com/avoluscars"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    className="h-11 w-11 inline-flex items-center justify-center border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
                  >
                    <Facebook size={16} />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.15} className="lg:col-span-3">
            {submitted ? (
              <div className="border border-border p-12 text-center bg-card">
                <h2 className="font-display text-3xl text-foreground mb-3">
                  Message received.
                </h2>
                <p className="text-foreground/70">
                  Thank you for reaching out. We'll be in touch shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="grid gap-6 md:grid-cols-2 border border-border p-8 md:p-10 bg-card"
              >
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-[0.25em] text-foreground/80">
                    Full Name
                  </Label>
                  <Input id="name" name="name" required maxLength={100} className="mt-2 h-12 rounded-none bg-background" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs uppercase tracking-[0.25em] text-foreground/80">
                    Phone
                  </Label>
                  <Input id="phone" name="phone" type="tel" maxLength={30} className="mt-2 h-12 rounded-none bg-background" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.25em] text-foreground/80">
                    Email
                  </Label>
                  <Input id="email" name="email" type="email" required maxLength={255} className="mt-2 h-12 rounded-none bg-background" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.25em] text-foreground/80">
                    Message
                  </Label>
                  <Textarea id="message" name="message" rows={6} required maxLength={2000} className="mt-2 rounded-none bg-background" />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>

            )}
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Our Location</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-foreground mb-3">
              Vancouver-based, delivery-first.
            </h2>
            <p className="text-foreground/70 max-w-2xl mb-8">
              Avolus has no physical pickup location. Every vehicle is delivered to you. Complimentary delivery within Vancouver and to YVR airport; all other Greater Vancouver and Whistler deliveries are available by arrangement.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-border overflow-hidden aspect-[16/9] w-full">
              <iframe
                title="Avolus Cars, Vancouver, BC"
                src={`https://www.google.com/maps?q=${encodeURIComponent(
                  "Avolus Luxury Car Rental, 250-997 Seymour St #1711, Vancouver, BC V6B 3M1"
                )}&z=14&output=embed`}
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full grayscale-[0.2]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      
    </Layout>
  );
}
